import { Injectable } from '@angular/core';
import { Observable, forkJoin, map, switchMap } from 'rxjs';
import { Permission } from '../enums/permission.enum';
import { PermissionService } from './permission.service';
import { PermissionApiService } from './permission-api.service';
import { RolePermissionApiService } from './role-permission-api.service';
import { RoleService } from './role.service';
import { Permission as PermissionModel, RolePermissionResponse } from '../models/permission';

// Interface for role permissions (matching the one in role-mgmt component)
export interface RolePermission {
  role: string;

  dashboard_global: boolean;
  dashboard_client: boolean;
  dashboard_leader: boolean;
  dashboard_language: boolean;
  dashboard_promotion: boolean;
  dashboard_medical_visit: boolean;

  cards_view_read: boolean;
  cards_view_write: boolean;

  overview_read: boolean;
  overview_write: boolean;
  basic_employee_info: boolean;

  civil_state_details_read: boolean;
  civil_state_details_write: boolean;

  location_details_read: boolean;
  location_details_write: boolean;

  social_security_details_read: boolean;
  social_security_details_write: boolean;

  contract_details_read: boolean;
  contract_details_write: boolean;

  career_details_read: boolean;
  career_details_write: boolean;

  education_read: boolean;
  education_write: boolean;

  languages_read: boolean;
  languages_write: boolean;

  financial_details_read: boolean;
  financial_details_write: boolean;

  salary_advances_read: boolean;
  salary_advances_write: boolean;

  afma_read: boolean;
  afma_write: boolean;

  documents_read: boolean;
  documents_write: boolean;

  equipment_read: boolean;
  equipment_write: boolean;

  medical_visit_read: boolean;
  medical_visit_write: boolean;

  pay_slip_read: boolean;
  pay_slip_write: boolean;

  certifications_download: boolean;

  skills_read: boolean;
  skills_write: boolean;

  card_view_validate_role: boolean;
  own_page: boolean;

  client_read: boolean;
  client_write: boolean;

  project_read: boolean;
  project_write: boolean;

  staffing_read: boolean;
  staffing_write: boolean;

  promotion_read: boolean;
  promotion_identify_candidate: boolean;
  promotion_confirm_identification: boolean;
  promotion_schedule_interview: boolean;
  promotion_manage_candidate: boolean;
  promotion_commit: boolean;

  evaluation_read: boolean;
  evaluation_write: boolean;
  evluation_assign_reviewers: boolean;

  pool_read: boolean;
  pool_write: boolean;

  manager_read: boolean;
  manager_write: boolean;
  manager_people: boolean;

  // BSA practice behaviour (cross-practice visibility, notification page actions)
  bsa: boolean;

  // Production menu access (Service, Unassigned people, People with supervisor)
  production: boolean;

  people_read: boolean;
  people_write: boolean;

  director_read: boolean;
  director_write: boolean;


  notification_read: boolean;

  salaries_read: boolean;
  salaries_write: boolean;
  salary_validate: boolean;

  config_staffing: boolean;
  config_bsa: boolean;
  config_invoicing: boolean;
  config_performance: boolean;
  config_user_management: boolean;

  invoicing_read: boolean;
  invoicing_service: boolean;
  invoicing_admin: boolean;
  extra_oncall_export: boolean;

  export_import_import_employee: boolean;
  export_import_import_employees: boolean;
  export_import_missing_documents: boolean;
  export_import_seniority: boolean;
  export_import_afma: boolean;
  export_import_medical_visit: boolean;
  export_import_school_grant: boolean;
  export_import_staffing_bsa: boolean;
  export_import_staffing_production: boolean;
  export_import_pay_slip: boolean;
  export_import_salary: boolean;
  config_export_import: boolean;

  bsa_org_read: boolean;
  bsa_org_write: boolean;

  audit_log_read: boolean;
  opportunity_read: boolean;
  opportunity_write: boolean;

  demand_read: boolean;
  demand_write: boolean;

  people_at_risk_read: boolean;
  people_at_risk_write: boolean;

  billing_read: boolean;
  billing_write: boolean;

  people_with_bad_attitude_read: boolean;
  people_with_bad_attitude_write: boolean;
  feedback_assigned_handler: boolean;
  feedback_assignment_manager: boolean;
  junior_promotion_notification: boolean;
  trial_period_ending_notification: boolean;
  contract_ending_notification: boolean;
  internship_request_read: boolean;
  internship_request_write: boolean;
  internship_people_interview_read: boolean;
  internship_people_interview_write: boolean;
  internship_technical_interview_read: boolean;
  internship_technical_interview_write: boolean;
  internship_documents_read: boolean;
  internship_documents_write: boolean;
  internship_credentials_read: boolean;
  internship_credentials_write: boolean;
  internship_feedback_read: boolean;
  internship_feedback_write: boolean;
  internship_lists_read: boolean;
  internship_lists_write: boolean;
}
export interface UserRole {
  id: number;
  name: string;
  description: string;
}

@Injectable({
  providedIn: 'root',
})
export class RolePermissionBridgeService {
  private permissionNameToIdMap: Record<string, number> = {};
  private permissionsLoaded = false;

  /** Role name -> id, filled by loadRolePermissionsFromApi() so saves skip a round trip. */
  private readonly roleIdByName = new Map<string, number>();

  constructor(
    private permissionService: PermissionService,
    private permissionApiService: PermissionApiService,
    private rolePermissionApiService: RolePermissionApiService,
    private roleService: RoleService
  ) {
    this.loadPermissionNameToIdMap();
  }

  /**
   * Converts role permissions to bitwise permission value
   * @param rolePermissions The role permission object with boolean flags
   * @returns BigInt representing the combined permissions
   */
  convertRoleToPermissions(rolePermissions: RolePermission): bigint {
    let permissions = 0n;

   // Dashboard permissions
    if (rolePermissions.dashboard_global) permissions |= Permission.DASHBOARD_GLOBAL;
    if (rolePermissions.dashboard_client) permissions |= Permission.DASHBOARD_CLIENT;
    if (rolePermissions.dashboard_leader) permissions |= Permission.DASHBOARD_LEADER;
    if (rolePermissions.dashboard_language) permissions |= Permission.DASHBOARD_LANGUAGE;
    if (rolePermissions.dashboard_promotion) permissions |= Permission.DASHBOARD_PROMOTION;
    if (rolePermissions.dashboard_medical_visit) permissions |= Permission.DASHBOARD_MEDICAL_VISIT;
    // Cards view permissions
    if (rolePermissions.cards_view_read) permissions |= Permission.CARDS_VIEW_READ;
    if (rolePermissions.cards_view_write) permissions |= Permission.CARDS_VIEW_READ | Permission.CARDS_VIEW_WRITE;

    // Other permissions
    if (rolePermissions.card_view_validate_role) permissions |= Permission.CARD_VIEW_VALIDATE_ROLE;
    if (rolePermissions.own_page) permissions |= Permission.CARD_VIEW_OWN_PAGE;

        // Employee card section permissions
    if (rolePermissions.overview_read) {
      permissions |= Permission.CARD_VIEW_OVERVIEW_READ;
    }
    if (rolePermissions.overview_write) {
  permissions |=
    Permission.CARD_VIEW_OVERVIEW_READ |
    Permission.CARD_VIEW_OVERVIEW_WRITE;
}
if (rolePermissions.basic_employee_info) {
  permissions |= Permission.CARD_VIEW_BASIC_EMPLOYEE_INFO;
}
    if (rolePermissions.civil_state_details_read) {
      permissions |= Permission.CARD_VIEW_CIVIL_STATE_DETAILS_READ;
    }
    if (rolePermissions.civil_state_details_write) {
      permissions |=
        Permission.CARD_VIEW_CIVIL_STATE_DETAILS_READ |
        Permission.CARD_VIEW_CIVIL_STATE_DETAILS_WRITE;
    }

    if (rolePermissions.location_details_read) {
      permissions |= Permission.CARD_VIEW_LOCATION_DETAILS_READ;
    }
    if (rolePermissions.location_details_write) {
      permissions |=
        Permission.CARD_VIEW_LOCATION_DETAILS_READ |
        Permission.CARD_VIEW_LOCATION_DETAILS_WRITE;
    }

    if (rolePermissions.social_security_details_read) {
      permissions |= Permission.CARD_VIEW_SOCIAL_SECURITY_DETAILS_READ;
    }
    if (rolePermissions.social_security_details_write) {
      permissions |=
        Permission.CARD_VIEW_SOCIAL_SECURITY_DETAILS_READ |
        Permission.CARD_VIEW_SOCIAL_SECURITY_DETAILS_WRITE;
    }

    if (rolePermissions.contract_details_read) {
      permissions |= Permission.CARD_VIEW_CONTRACT_DETAILS_READ;
    }
    if (rolePermissions.contract_details_write) {
      permissions |=
        Permission.CARD_VIEW_CONTRACT_DETAILS_READ |
        Permission.CARD_VIEW_CONTRACT_DETAILS_WRITE;
    }

   if (rolePermissions.career_details_read) {
  permissions |= Permission.CARD_VIEW_CAREER_DETAILS;
}

if (rolePermissions.career_details_write) {
  permissions |=
    Permission.CARD_VIEW_CAREER_DETAILS |
    Permission.CARD_VIEW_CAREER_DETAILS_WRITE;
}

    if (rolePermissions.education_read) {
      permissions |= Permission.CARD_VIEW_EDUCATION_READ;
    }
    if (rolePermissions.education_write) {
      permissions |=
        Permission.CARD_VIEW_EDUCATION_READ |
        Permission.CARD_VIEW_EDUCATION_WRITE;
    }

    if (rolePermissions.languages_read) {
      permissions |= Permission.CARD_VIEW_LANGUAGES_READ;
    }
    if (rolePermissions.languages_write) {
      permissions |=
        Permission.CARD_VIEW_LANGUAGES_READ |
        Permission.CARD_VIEW_LANGUAGES_WRITE;
    }

    if (rolePermissions.financial_details_read) {
      permissions |= Permission.CARD_VIEW_FINANCIAL_DETAILS_READ;
    }
    if (rolePermissions.financial_details_write) {
      permissions |=
        Permission.CARD_VIEW_FINANCIAL_DETAILS_READ |
        Permission.CARD_VIEW_FINANCIAL_DETAILS_WRITE;
    }

    if (rolePermissions.salary_advances_read) {
      permissions |= Permission.CARD_VIEW_SALARY_ADVANCES_READ;
    }

    if (rolePermissions.salary_advances_write) {
      permissions |=
        Permission.CARD_VIEW_SALARY_ADVANCES_READ |
        Permission.CARD_VIEW_SALARY_ADVANCES_WRITE;
        }

        if (rolePermissions.afma_read) {
      permissions |= Permission.CARD_VIEW_AFMA_READ;
    }

    if (rolePermissions.afma_write) {
      permissions |= Permission.CARD_VIEW_AFMA_WRITE;
        }

    if (rolePermissions.documents_read) {
      permissions |= Permission.CARD_VIEW_DOCUMENTS_READ;
    }
    if (rolePermissions.documents_write) {
      permissions |=
        Permission.CARD_VIEW_DOCUMENTS_READ |
        Permission.CARD_VIEW_DOCUMENTS_WRITE;
    }

    if (rolePermissions.equipment_read) {
      permissions |= Permission.CARD_VIEW_EQUIPMENT_READ;
    }
    if (rolePermissions.equipment_write) {
      permissions |=
        Permission.CARD_VIEW_EQUIPMENT_READ |
        Permission.CARD_VIEW_EQUIPMENT_WRITE;
    }

    if (rolePermissions.medical_visit_read) {
      permissions |= Permission.CARD_VIEW_MEDICAL_VISIT_READ;
    }
    if (rolePermissions.medical_visit_write) {
      permissions |=
        Permission.CARD_VIEW_MEDICAL_VISIT_READ |
        Permission.CARD_VIEW_MEDICAL_VISIT_WRITE;
    }

    if (rolePermissions.pay_slip_read) {
      permissions |= Permission.CARD_VIEW_PAY_SLIP_READ;
    }
    if (rolePermissions.pay_slip_write) {
      permissions |=
        Permission.CARD_VIEW_PAY_SLIP_READ |
        Permission.CARD_VIEW_PAY_SLIP_WRITE;
    }

    if (rolePermissions.certifications_download) {
      permissions |= Permission.CARD_VIEW_CERTIFICATIONS_DOWNLOAD;
    }

    if (rolePermissions.skills_read) {
      permissions |= Permission.CARD_VIEW_SKILLS_READ;
    }
    if (rolePermissions.skills_write) {
      permissions |=
        Permission.CARD_VIEW_SKILLS_READ |
        Permission.CARD_VIEW_SKILLS_WRITE;
    }

    // Client permissions
    if (rolePermissions.client_read) permissions |= Permission.CLIENT_READ;
    if (rolePermissions.client_write) permissions |= Permission.CLIENT_READ | Permission.CLIENT_WRITE;

    // Project permissions
    if (rolePermissions.project_read) permissions |= Permission.PROJECT_READ;
    if (rolePermissions.project_write) permissions |= Permission.PROJECT_READ | Permission.PROJECT_WRITE;

    // Staffing permissions
    if (rolePermissions.staffing_read) permissions |= Permission.STAFFING_READ;
    if (rolePermissions.staffing_write) permissions |= Permission.STAFFING_READ | Permission.STAFFING_WRITE;

    // Promotion permissions
    if (rolePermissions.promotion_read) permissions |= Permission.PROMOTION_READ;
    if (rolePermissions.promotion_identify_candidate) permissions |= Permission.PROMOTION_READ | Permission.PROMOTION_IDENTIFY_CANDIDATE;
    if (rolePermissions.promotion_confirm_identification) permissions |= Permission.PROMOTION_CONFIRM_IDENTIFICATION;
    if (rolePermissions.promotion_schedule_interview) permissions |= Permission.PROMOTION_SCHEDULE_INTERVIEW;
    if (rolePermissions.promotion_manage_candidate) permissions |= Permission.PROMOTION_MANAGE_CANDIDATE;
    if (rolePermissions.promotion_commit)permissions |= Permission.PROMOTION_COMMIT;

    // Evaluation permissions
    if (rolePermissions.evaluation_read) permissions |= Permission.EVALUATION_READ;
    if (rolePermissions.evaluation_write) permissions |= Permission.EVALUATION_READ | Permission.EVALUATION_WRITE;
    if (rolePermissions.evluation_assign_reviewers) permissions |= Permission.EVLUATION_ASSIGN_REVIEWERS;

    // Pool permissions
    if (rolePermissions.pool_read) permissions |= Permission.POOL_READ;
    if (rolePermissions.pool_write) permissions |= Permission.POOL_READ | Permission.POOL_WRITE;
    // Manager permissions
    if (rolePermissions.manager_read) {
      permissions |= Permission.MANAGER_READ;
    }

    if (rolePermissions.manager_write) {
      permissions |= Permission.MANAGER_READ | Permission.MANAGER_WRITE;
    }

    if (rolePermissions.manager_people) {
      permissions |= Permission.MANAGER_PEOPLE;
    }

    if (rolePermissions.bsa) {
      permissions |= Permission.BSA;
    }

    if (rolePermissions.production) {
      permissions |= Permission.PRODUCTION;
    }

    // People permissions
    if (rolePermissions.people_read) {
      permissions |= Permission.PEOPLE_READ;
    }

    if (rolePermissions.people_write) {
      permissions |= Permission.PEOPLE_READ | Permission.PEOPLE_WRITE;
    }
    // Director permissions
    if (rolePermissions.director_read) permissions |= Permission.DIRECTOR_READ;
    if (rolePermissions.director_write) permissions |= Permission.DIRECTOR_READ | Permission.DIRECTOR_WRITE;

    // Notification permissions
    if (rolePermissions.notification_read) permissions |= Permission.NOTIFICATION_READ;
    if (rolePermissions.junior_promotion_notification) permissions |= Permission.JUNIOR_PROMOTION_NOTIFICATION;
    if (rolePermissions.trial_period_ending_notification) permissions |= Permission.TRIAL_PERIOD_ENDING_NOTIFICATION;
    if (rolePermissions.contract_ending_notification) permissions |= Permission.CONTRACT_ENDING_NOTIFICATION;

    // Salaries permissions (separate from general salaries)
    if (rolePermissions.salaries_read) permissions |= Permission.SALARIES_READ;
    if (rolePermissions.salaries_write) permissions |= Permission.SALARIES_WRITE;
    if (rolePermissions.salary_validate) permissions |= Permission.SALARY_VALIDATE;

    // Configuration domain permissions
    if (rolePermissions.config_staffing) permissions |= Permission.CONFIG_STAFFING;
    if (rolePermissions.config_bsa) permissions |= Permission.CONFIG_BSA;
    if (rolePermissions.config_invoicing) permissions |= Permission.CONFIG_INVOICING;
    if (rolePermissions.config_performance) permissions |= Permission.CONFIG_PERFORMANCE;
    if (rolePermissions.config_user_management) permissions |= Permission.CONFIG_USER_MANAGEMENT;

    // Invoicing permissions
    if (rolePermissions.invoicing_read) permissions |= Permission.INVOICING_READ;
    if (rolePermissions.invoicing_service) {
      permissions |= Permission.INVOICING_READ | Permission.INVOICING_SERVICE;
    }
    if (rolePermissions.invoicing_admin) {
      permissions |= Permission.INVOICING_READ | Permission.INVOICING_SERVICE | Permission.INVOICING_ADMIN;
    }
    if (rolePermissions.extra_oncall_export) {
      permissions |= Permission.Extra_OnCall_Export;
    }

    // Export/Import permissions are independent of each other - granting one
    // never implies another, so each maps straight through.
    if (rolePermissions.export_import_import_employee) {
      permissions |= Permission.EXPORT_IMPORT_IMPORT_EMPLOYEE;
    }
    if (rolePermissions.export_import_import_employees) {
      permissions |= Permission.EXPORT_IMPORT_IMPORT_EMPLOYEES;
    }
    if (rolePermissions.export_import_missing_documents) {
      permissions |= Permission.EXPORT_IMPORT_MISSING_DOCUMENTS;
    }
    if (rolePermissions.export_import_seniority) {
      permissions |= Permission.EXPORT_IMPORT_SENIORITY;
    }
    if (rolePermissions.export_import_afma) {
      permissions |= Permission.EXPORT_IMPORT_AFMA;
    }
    if (rolePermissions.export_import_medical_visit) {
      permissions |= Permission.EXPORT_IMPORT_MEDICAL_VISIT;
    }
    if (rolePermissions.export_import_school_grant) {
      permissions |= Permission.EXPORT_IMPORT_SCHOOL_GRANT;
    }
    if (rolePermissions.export_import_staffing_bsa) {
      permissions |= Permission.EXPORT_IMPORT_STAFFING_BSA;
    }
    if (rolePermissions.export_import_staffing_production) {
      permissions |= Permission.EXPORT_IMPORT_STAFFING_PRODUCTION;
    }
    if (rolePermissions.export_import_pay_slip) {
      permissions |= Permission.EXPORT_IMPORT_PAY_SLIP;
    }
    if (rolePermissions.export_import_salary) {
      permissions |= Permission.EXPORT_IMPORT_SALARY;
    }
    if (rolePermissions.config_export_import) {
      permissions |= Permission.CONFIG_EXPORT_IMPORT;
    }

    // BSA ORG permissions
    if (rolePermissions.bsa_org_read) permissions |= Permission.BSA_ORG_READ;
    if (rolePermissions.bsa_org_write) permissions |= Permission.BSA_ORG_READ | Permission.BSA_ORG_WRITE;

    // Audit Log permissions
    if (rolePermissions.audit_log_read) permissions |= Permission.AUDIT_LOG_READ;

    // Feedback permissions
    if (rolePermissions.feedback_assigned_handler) permissions |= Permission.FEEDBACK_ASSIGNED_HANDLER;
    if (rolePermissions.feedback_assignment_manager) permissions |= Permission.FEEDBACK_ASSIGNMENT_MANAGER;

    // Opportunities permissions
    if (rolePermissions.opportunity_read) permissions |= Permission.OPPORTUNITY_READ;
    if (rolePermissions.opportunity_write) permissions |= Permission.OPPORTUNITY_READ | Permission.OPPORTUNITY_WRITE;
    if (rolePermissions.demand_read) permissions |= Permission.DEMAND_READ;
    if (rolePermissions.demand_write) permissions |= Permission.DEMAND_READ | Permission.DEMAND_WRITE;
    if (rolePermissions.people_at_risk_read) permissions |= Permission.PEOPLE_AT_RISK_READ;
    if (rolePermissions.people_at_risk_write) permissions |= Permission.PEOPLE_AT_RISK_READ | Permission.PEOPLE_AT_RISK_WRITE;
    if (rolePermissions.billing_read) permissions |= Permission.BILLING_READ;
    if (rolePermissions.billing_write) permissions |= Permission.BILLING_READ | Permission.BILLING_WRITE;
    if (rolePermissions.people_with_bad_attitude_read) permissions |= Permission.PEOPLE_WITH_BAD_ATTITUDE_READ;
    if (rolePermissions.people_with_bad_attitude_write) permissions |= Permission.PEOPLE_WITH_BAD_ATTITUDE_READ | Permission.PEOPLE_WITH_BAD_ATTITUDE_WRITE;
        // Internship Management permissions
    if (rolePermissions.internship_request_read) permissions |= Permission.INTERNSHIP_REQUEST_READ;
    if (rolePermissions.internship_request_write) permissions |= Permission.INTERNSHIP_REQUEST_READ | Permission.INTERNSHIP_REQUEST_WRITE;
    if (rolePermissions.internship_people_interview_read) permissions |= Permission.INTERNSHIP_PEOPLE_INTERVIEW_READ;
    if (rolePermissions.internship_people_interview_write) permissions |= Permission.INTERNSHIP_PEOPLE_INTERVIEW_READ | Permission.INTERNSHIP_PEOPLE_INTERVIEW_WRITE;
    if (rolePermissions.internship_technical_interview_read) permissions |= Permission.INTERNSHIP_TECHNICAL_INTERVIEW_READ;
    if (rolePermissions.internship_technical_interview_write) permissions |= Permission.INTERNSHIP_TECHNICAL_INTERVIEW_READ | Permission.INTERNSHIP_TECHNICAL_INTERVIEW_WRITE;
    if (rolePermissions.internship_documents_read) permissions |= Permission.INTERNSHIP_DOCUMENTS_READ;
    if (rolePermissions.internship_documents_write) permissions |= Permission.INTERNSHIP_DOCUMENTS_READ | Permission.INTERNSHIP_DOCUMENTS_WRITE;
    if (rolePermissions.internship_credentials_read) permissions |= Permission.INTERNSHIP_CREDENTIALS_READ;
    if (rolePermissions.internship_credentials_write) permissions |= Permission.INTERNSHIP_CREDENTIALS_READ | Permission.INTERNSHIP_CREDENTIALS_WRITE;
    if (rolePermissions.internship_feedback_read) permissions |= Permission.INTERNSHIP_FEEDBACK_READ;
    if (rolePermissions.internship_feedback_write) permissions |= Permission.INTERNSHIP_FEEDBACK_READ | Permission.INTERNSHIP_FEEDBACK_WRITE;
    if (rolePermissions.internship_lists_read) permissions |= Permission.INTERNSHIP_LISTS_READ;
    if (rolePermissions.internship_lists_write) permissions |= Permission.INTERNSHIP_LISTS_READ | Permission.INTERNSHIP_LISTS_WRITE;
    return permissions;
  }

  /**
   * Converts multiple role permissions to combined bitwise permissions
   * @param userRoles Array of user roles
   * @param rolePermissions Array of all role permissions
   * @returns Combined BigInt permissions for all user roles
   */
  convertUserRolesToPermissions(userRoles: UserRole[], rolePermissions: RolePermission[]): bigint {
    let combinedPermissions = 0n;

    userRoles.forEach(userRole => {
      const rolePermission = rolePermissions.find(rp => rp.role === userRole.name);
      if (rolePermission) {
        combinedPermissions |= this.convertRoleToPermissions(rolePermission);
      }
    });

    return combinedPermissions;
  }

  /**
   * Updates user permissions based on their assigned roles
   * @param userRoles Array of user roles
   * @param rolePermissions Array of all role permissions
   */
  updateUserPermissions(userRoles: UserRole[], rolePermissions: RolePermission[]): void {
    const permissions = this.convertUserRolesToPermissions(userRoles, rolePermissions);
    this.permissionService.updatePermissions(permissions);
  }

  /**
   * Gets permission names from a role permission object
   * @param rolePermissions The role permission object
   * @returns Array of permission names that are enabled
   */
  getEnabledPermissionNames(rolePermissions: RolePermission): string[] {
    const enabledPermissions: string[] = [];

    Object.entries(rolePermissions).forEach(([key, value]) => {
      if (key !== 'role' && value === true) {
        enabledPermissions.push(key);
      }
    });

    return enabledPermissions;
  }

  /**
   * Checks if a role has a specific permission
   * @param rolePermissions The role permission object
   * @param permissionName The permission name to check
   * @returns True if the role has the permission
   */
  roleHasPermission(rolePermissions: RolePermission, permissionName: string): boolean {
    return (rolePermissions as any)[permissionName] === true;
  }

  /**
   * Merges permissions from multiple roles
   * @param rolePermissionsList Array of role permission objects
   * @returns Merged role permissions with OR logic
   */
  mergeRolePermissions(rolePermissionsList: RolePermission[]): Omit<RolePermission, 'role'> {
    const merged: any = {
      dashboard_global: false,
      dashboard_client: false,
      dashboard_leader: false,
      dashboard_language: false,
      dashboard_promotion: false,
      dashboard_medical_visit: false,
      cards_view_read: false,
      cards_view_write: false,
      overview_read: false,
      overview_write: false,
      basic_employee_info: false,
      civil_state_details_read: false,
      civil_state_details_write: false,
      location_details_read: false,
      location_details_write: false,
      social_security_details_read: false,
      social_security_details_write: false,
      contract_details_read: false,
      contract_details_write: false,
      career_details_read: false,
      career_details_write: false,
      education_read: false,
      education_write: false,
      languages_read: false,
      languages_write: false,
      financial_details_read: false,
      financial_details_write: false,
      salary_advances_read: false,
      salary_advances_write: false,
      afma_read: false,
      afma_write: false,
      documents_read: false,
      documents_write: false,
      equipment_read: false,
      equipment_write: false,
      medical_visit_read: false,
      medical_visit_write: false,
      pay_slip_read: false,
      pay_slip_write: false,
      certifications_download: false,
      skills_read: false,
      skills_write: false,
      card_view_validate_role: false,
      own_page: false,
      client_read: false,
      client_write: false,
      project_read: false,
      project_write: false,
      staffing_read: false,
      staffing_write: false,
      promotion_read: false,
      promotion_identify_candidate: false,
      promotion_confirm_identification: false,
      promotion_schedule_interview: false,
      promotion_manage_candidate: false,
      promotion_commit: false,
      evaluation_read: false,
      evaluation_write: false,
      evluation_assign_reviewers: false,
      pool_read: false,
      pool_write: false,
      manager_read: false,
      manager_write: false,
      manager_people: false,
      bsa: false,
      production: false,
      people_read: false,
      people_write: false,
      director_read: false,
      director_write: false,
      notification_read: false,
      junior_promotion_notification: false,
      trial_period_ending_notification: false,
      contract_ending_notification: false,
      salaries_read: false,
      salaries_write: false,
      salary_validate: false,
      config_staffing: false,
      config_bsa: false,
      config_invoicing: false,
      config_performance: false,
      config_user_management: false,
      invoicing_read: false,
      invoicing_service: false,
      invoicing_admin: false,
      extra_oncall_export: false,

      export_import_import_employee: false,
      export_import_import_employees: false,
      export_import_missing_documents: false,
      export_import_seniority: false,
      export_import_afma: false,
      export_import_medical_visit: false,
      export_import_school_grant: false,
      export_import_staffing_bsa: false,
      export_import_staffing_production: false,
      export_import_pay_slip: false,
      export_import_salary: false,
      config_export_import: false,
      bsa_org_read: false,
      bsa_org_write: false,
      audit_log_read: false,
      opportunity_read: false,
      opportunity_write: false,
      demand_read: false,
      demand_write: false,
      people_at_risk_read: false,
      people_at_risk_write: false,
      billing_read: false,
      billing_write: false,
      people_with_bad_attitude_read: false,
      people_with_bad_attitude_write: false,
      feedback_assigned_handler: false,
      feedback_assignment_manager: false,
      internship_request_read: false,
      internship_request_write: false,
      internship_people_interview_read: false,
      internship_people_interview_write: false,
      internship_technical_interview_read: false,
      internship_technical_interview_write: false,
      internship_documents_read: false,
      internship_documents_write: false,
      internship_credentials_read: false,
      internship_credentials_write: false,
      internship_feedback_read: false,
      internship_feedback_write: false,
      internship_lists_read: false,
      internship_lists_write: false,
    };

    rolePermissionsList.forEach(rolePermissions => {
      Object.keys(merged).forEach(key => {
        if ((rolePermissions as any)[key]) {
          merged[key] = true;
        }
      });
    });

    return merged;
  }

  /**
   * Converts bitwise permissions back to role permission boolean flags
   * @param permissions BigInt permissions value
   * @returns Role permission object with boolean flags
   */
  convertPermissionsToRoleFlags(permissions: bigint): Omit<RolePermission, 'role'> {
    return {
      dashboard_global:
        (permissions & Permission.DASHBOARD_GLOBAL) === Permission.DASHBOARD_GLOBAL,
      dashboard_client:
        (permissions & Permission.DASHBOARD_CLIENT) === Permission.DASHBOARD_CLIENT,
      dashboard_leader:
        (permissions & Permission.DASHBOARD_LEADER) === Permission.DASHBOARD_LEADER,
      dashboard_language:
        (permissions & Permission.DASHBOARD_LANGUAGE) === Permission.DASHBOARD_LANGUAGE,
      dashboard_promotion:
        (permissions & Permission.DASHBOARD_PROMOTION) === Permission.DASHBOARD_PROMOTION,
      dashboard_medical_visit:
        (permissions & Permission.DASHBOARD_MEDICAL_VISIT) === Permission.DASHBOARD_MEDICAL_VISIT,
      cards_view_read: (permissions & Permission.CARDS_VIEW_READ) === Permission.CARDS_VIEW_READ,
      cards_view_write: (permissions & Permission.CARDS_VIEW_WRITE) === Permission.CARDS_VIEW_WRITE,
      overview_read:
        (permissions & Permission.CARD_VIEW_OVERVIEW_READ) ===
        Permission.CARD_VIEW_OVERVIEW_READ,

      overview_write:
        (permissions & Permission.CARD_VIEW_OVERVIEW_WRITE) ===
        Permission.CARD_VIEW_OVERVIEW_WRITE,

      basic_employee_info:
  (permissions & Permission.CARD_VIEW_BASIC_EMPLOYEE_INFO) ===
  Permission.CARD_VIEW_BASIC_EMPLOYEE_INFO,

      civil_state_details_read:
        (permissions & Permission.CARD_VIEW_CIVIL_STATE_DETAILS_READ) ===
        Permission.CARD_VIEW_CIVIL_STATE_DETAILS_READ,

      civil_state_details_write:
        (permissions & Permission.CARD_VIEW_CIVIL_STATE_DETAILS_WRITE) ===
        Permission.CARD_VIEW_CIVIL_STATE_DETAILS_WRITE,

      location_details_read:
        (permissions & Permission.CARD_VIEW_LOCATION_DETAILS_READ) ===
        Permission.CARD_VIEW_LOCATION_DETAILS_READ,

      location_details_write:
        (permissions & Permission.CARD_VIEW_LOCATION_DETAILS_WRITE) ===
        Permission.CARD_VIEW_LOCATION_DETAILS_WRITE,

      social_security_details_read:
        (permissions & Permission.CARD_VIEW_SOCIAL_SECURITY_DETAILS_READ) ===
        Permission.CARD_VIEW_SOCIAL_SECURITY_DETAILS_READ,

      social_security_details_write:
        (permissions & Permission.CARD_VIEW_SOCIAL_SECURITY_DETAILS_WRITE) ===
        Permission.CARD_VIEW_SOCIAL_SECURITY_DETAILS_WRITE,

      contract_details_read:
        (permissions & Permission.CARD_VIEW_CONTRACT_DETAILS_READ) ===
        Permission.CARD_VIEW_CONTRACT_DETAILS_READ,

      contract_details_write:
        (permissions & Permission.CARD_VIEW_CONTRACT_DETAILS_WRITE) ===
        Permission.CARD_VIEW_CONTRACT_DETAILS_WRITE,

      career_details_read:
        (permissions & Permission.CARD_VIEW_CAREER_DETAILS) ===
        Permission.CARD_VIEW_CAREER_DETAILS,

      career_details_write:
        (permissions & Permission.CARD_VIEW_CAREER_DETAILS_WRITE) ===
        Permission.CARD_VIEW_CAREER_DETAILS_WRITE,

      education_read:
        (permissions & Permission.CARD_VIEW_EDUCATION_READ) ===
        Permission.CARD_VIEW_EDUCATION_READ,

      education_write:
        (permissions & Permission.CARD_VIEW_EDUCATION_WRITE) ===
        Permission.CARD_VIEW_EDUCATION_WRITE,

      languages_read:
        (permissions & Permission.CARD_VIEW_LANGUAGES_READ) ===
        Permission.CARD_VIEW_LANGUAGES_READ,

      languages_write:
        (permissions & Permission.CARD_VIEW_LANGUAGES_WRITE) ===
        Permission.CARD_VIEW_LANGUAGES_WRITE,

      financial_details_read:
        (permissions & Permission.CARD_VIEW_FINANCIAL_DETAILS_READ) ===
        Permission.CARD_VIEW_FINANCIAL_DETAILS_READ,

      financial_details_write:
        (permissions & Permission.CARD_VIEW_FINANCIAL_DETAILS_WRITE) ===
        Permission.CARD_VIEW_FINANCIAL_DETAILS_WRITE,


      salary_advances_read:
      (permissions & Permission.CARD_VIEW_SALARY_ADVANCES_READ) ===
      Permission.CARD_VIEW_SALARY_ADVANCES_READ,

       salary_advances_write:
      (permissions & Permission.CARD_VIEW_SALARY_ADVANCES_WRITE) ===
        Permission.CARD_VIEW_SALARY_ADVANCES_WRITE,

            afma_read:
        (permissions & Permission.CARD_VIEW_AFMA_READ) ===
        Permission.CARD_VIEW_AFMA_READ,

      afma_write:
        (permissions & Permission.CARD_VIEW_AFMA_WRITE) ===
        Permission.CARD_VIEW_AFMA_WRITE,

      documents_read:
        (permissions & Permission.CARD_VIEW_DOCUMENTS_READ) ===
        Permission.CARD_VIEW_DOCUMENTS_READ,

      documents_write:
        (permissions & Permission.CARD_VIEW_DOCUMENTS_WRITE) ===
        Permission.CARD_VIEW_DOCUMENTS_WRITE,

      equipment_read:
        (permissions & Permission.CARD_VIEW_EQUIPMENT_READ) ===
        Permission.CARD_VIEW_EQUIPMENT_READ,

      equipment_write:
        (permissions & Permission.CARD_VIEW_EQUIPMENT_WRITE) ===
        Permission.CARD_VIEW_EQUIPMENT_WRITE,

      medical_visit_read:
        (permissions & Permission.CARD_VIEW_MEDICAL_VISIT_READ) ===
        Permission.CARD_VIEW_MEDICAL_VISIT_READ,

      medical_visit_write:
        (permissions & Permission.CARD_VIEW_MEDICAL_VISIT_WRITE) ===
        Permission.CARD_VIEW_MEDICAL_VISIT_WRITE,

      pay_slip_read:
        (permissions & Permission.CARD_VIEW_PAY_SLIP_READ) ===
        Permission.CARD_VIEW_PAY_SLIP_READ,

      pay_slip_write:
        (permissions & Permission.CARD_VIEW_PAY_SLIP_WRITE) ===
        Permission.CARD_VIEW_PAY_SLIP_WRITE,

      certifications_download:
        (permissions & Permission.CARD_VIEW_CERTIFICATIONS_DOWNLOAD) ===
        Permission.CARD_VIEW_CERTIFICATIONS_DOWNLOAD,

      skills_read:
        (permissions & Permission.CARD_VIEW_SKILLS_READ) ===
        Permission.CARD_VIEW_SKILLS_READ,

      skills_write:
        (permissions & Permission.CARD_VIEW_SKILLS_WRITE) ===
              Permission.CARD_VIEW_SKILLS_WRITE,
      card_view_validate_role: (permissions & Permission.CARD_VIEW_VALIDATE_ROLE) === Permission.CARD_VIEW_VALIDATE_ROLE,
      own_page: (permissions & Permission.CARD_VIEW_OWN_PAGE) === Permission.CARD_VIEW_OWN_PAGE,
      client_read: (permissions & Permission.CLIENT_READ) === Permission.CLIENT_READ,
      client_write: (permissions & Permission.CLIENT_WRITE) === Permission.CLIENT_WRITE,
      project_read: (permissions & Permission.PROJECT_READ) === Permission.PROJECT_READ,
      project_write: (permissions & Permission.PROJECT_WRITE) === Permission.PROJECT_WRITE,
      staffing_read: (permissions & Permission.STAFFING_READ) === Permission.STAFFING_READ,
      staffing_write: (permissions & Permission.STAFFING_WRITE) === Permission.STAFFING_WRITE,
      promotion_read: (permissions & Permission.PROMOTION_READ) === Permission.PROMOTION_READ,
      promotion_identify_candidate: (permissions & Permission.PROMOTION_IDENTIFY_CANDIDATE) === Permission.PROMOTION_IDENTIFY_CANDIDATE,
      promotion_confirm_identification: (permissions & Permission.PROMOTION_CONFIRM_IDENTIFICATION) === Permission.PROMOTION_CONFIRM_IDENTIFICATION,
      promotion_schedule_interview: (permissions & Permission.PROMOTION_SCHEDULE_INTERVIEW) === Permission.PROMOTION_SCHEDULE_INTERVIEW,
      promotion_manage_candidate: (permissions & Permission.PROMOTION_MANAGE_CANDIDATE) === Permission.PROMOTION_MANAGE_CANDIDATE,
      promotion_commit:  (permissions & Permission.PROMOTION_COMMIT) ===Permission.PROMOTION_COMMIT,
      evaluation_read: (permissions & Permission.EVALUATION_READ) === Permission.EVALUATION_READ,
      evaluation_write: (permissions & Permission.EVALUATION_WRITE) === Permission.EVALUATION_WRITE,
      evluation_assign_reviewers: (permissions & Permission.EVLUATION_ASSIGN_REVIEWERS) === Permission.EVLUATION_ASSIGN_REVIEWERS,
      pool_read: (permissions & Permission.POOL_READ) === Permission.POOL_READ,
      pool_write: (permissions & Permission.POOL_WRITE) === Permission.POOL_WRITE,
      manager_read: (permissions & Permission.MANAGER_READ) === Permission.MANAGER_READ,
      manager_write: (permissions & Permission.MANAGER_WRITE) === Permission.MANAGER_WRITE,
      manager_people: (permissions & Permission.MANAGER_PEOPLE) === Permission.MANAGER_PEOPLE,
      bsa: (permissions & Permission.BSA) === Permission.BSA,
      production: (permissions & Permission.PRODUCTION) === Permission.PRODUCTION,
      people_read: (permissions & Permission.PEOPLE_READ) === Permission.PEOPLE_READ,
      people_write: (permissions & Permission.PEOPLE_WRITE) === Permission.PEOPLE_WRITE,
      director_read: (permissions & Permission.DIRECTOR_READ) === Permission.DIRECTOR_READ,
      director_write: (permissions & Permission.DIRECTOR_WRITE) === Permission.DIRECTOR_WRITE,
      notification_read: (permissions & Permission.NOTIFICATION_READ) === Permission.NOTIFICATION_READ,
      salaries_read: (permissions & Permission.SALARIES_READ) === Permission.SALARIES_READ,
      salaries_write: (permissions & Permission.SALARIES_WRITE) === Permission.SALARIES_WRITE,
      salary_validate: (permissions & Permission.SALARY_VALIDATE) === Permission.SALARY_VALIDATE,
      config_staffing: (permissions & Permission.CONFIG_STAFFING) === Permission.CONFIG_STAFFING,
      config_bsa: (permissions & Permission.CONFIG_BSA) === Permission.CONFIG_BSA,
      config_invoicing: (permissions & Permission.CONFIG_INVOICING) === Permission.CONFIG_INVOICING,
      config_performance: (permissions & Permission.CONFIG_PERFORMANCE) === Permission.CONFIG_PERFORMANCE,
      config_user_management: (permissions & Permission.CONFIG_USER_MANAGEMENT) === Permission.CONFIG_USER_MANAGEMENT,
      invoicing_read: (permissions & Permission.INVOICING_READ) === Permission.INVOICING_READ,
      invoicing_service: (permissions & Permission.INVOICING_SERVICE) === Permission.INVOICING_SERVICE,
      invoicing_admin: (permissions & Permission.INVOICING_ADMIN) === Permission.INVOICING_ADMIN,
      extra_oncall_export:
        (permissions & Permission.Extra_OnCall_Export) === Permission.Extra_OnCall_Export,

      export_import_import_employee:
        (permissions & Permission.EXPORT_IMPORT_IMPORT_EMPLOYEE) === Permission.EXPORT_IMPORT_IMPORT_EMPLOYEE,
      export_import_import_employees:
        (permissions & Permission.EXPORT_IMPORT_IMPORT_EMPLOYEES) === Permission.EXPORT_IMPORT_IMPORT_EMPLOYEES,
      export_import_missing_documents:
        (permissions & Permission.EXPORT_IMPORT_MISSING_DOCUMENTS) === Permission.EXPORT_IMPORT_MISSING_DOCUMENTS,
      export_import_seniority:
        (permissions & Permission.EXPORT_IMPORT_SENIORITY) === Permission.EXPORT_IMPORT_SENIORITY,
      export_import_afma:
        (permissions & Permission.EXPORT_IMPORT_AFMA) === Permission.EXPORT_IMPORT_AFMA,
      export_import_medical_visit:
        (permissions & Permission.EXPORT_IMPORT_MEDICAL_VISIT) === Permission.EXPORT_IMPORT_MEDICAL_VISIT,
      export_import_school_grant:
        (permissions & Permission.EXPORT_IMPORT_SCHOOL_GRANT) === Permission.EXPORT_IMPORT_SCHOOL_GRANT,
      export_import_staffing_bsa:
        (permissions & Permission.EXPORT_IMPORT_STAFFING_BSA) === Permission.EXPORT_IMPORT_STAFFING_BSA,
      export_import_staffing_production:
        (permissions & Permission.EXPORT_IMPORT_STAFFING_PRODUCTION) === Permission.EXPORT_IMPORT_STAFFING_PRODUCTION,
      export_import_pay_slip:
        (permissions & Permission.EXPORT_IMPORT_PAY_SLIP) === Permission.EXPORT_IMPORT_PAY_SLIP,
      export_import_salary:
        (permissions & Permission.EXPORT_IMPORT_SALARY) === Permission.EXPORT_IMPORT_SALARY,
      config_export_import:
        (permissions & Permission.CONFIG_EXPORT_IMPORT) === Permission.CONFIG_EXPORT_IMPORT,

      audit_log_read: (permissions & Permission.AUDIT_LOG_READ) === Permission.AUDIT_LOG_READ,
      bsa_org_read: (permissions & Permission.BSA_ORG_READ) === Permission.BSA_ORG_READ,
      bsa_org_write: (permissions & Permission.BSA_ORG_WRITE) === Permission.BSA_ORG_WRITE,
      opportunity_read: (permissions & Permission.OPPORTUNITY_READ) === Permission.OPPORTUNITY_READ,
      opportunity_write: (permissions & Permission.OPPORTUNITY_WRITE) === Permission.OPPORTUNITY_WRITE,
      demand_read: (permissions & Permission.DEMAND_READ) === Permission.DEMAND_READ,
      demand_write: (permissions & Permission.DEMAND_WRITE) === Permission.DEMAND_WRITE,
      people_at_risk_read: (permissions & Permission.PEOPLE_AT_RISK_READ) === Permission.PEOPLE_AT_RISK_READ,
      people_at_risk_write: (permissions & Permission.PEOPLE_AT_RISK_WRITE) === Permission.PEOPLE_AT_RISK_WRITE,
      billing_read: (permissions & Permission.BILLING_READ) === Permission.BILLING_READ,
      billing_write: (permissions & Permission.BILLING_WRITE) === Permission.BILLING_WRITE,
      people_with_bad_attitude_read: (permissions & Permission.PEOPLE_WITH_BAD_ATTITUDE_READ) === Permission.PEOPLE_WITH_BAD_ATTITUDE_READ,
      people_with_bad_attitude_write: (permissions & Permission.PEOPLE_WITH_BAD_ATTITUDE_WRITE) === Permission.PEOPLE_WITH_BAD_ATTITUDE_WRITE,
      feedback_assigned_handler: (permissions & Permission.FEEDBACK_ASSIGNED_HANDLER) === Permission.FEEDBACK_ASSIGNED_HANDLER,
      feedback_assignment_manager: (permissions & Permission.FEEDBACK_ASSIGNMENT_MANAGER) === Permission.FEEDBACK_ASSIGNMENT_MANAGER,
      junior_promotion_notification: (permissions & Permission.JUNIOR_PROMOTION_NOTIFICATION) === Permission.JUNIOR_PROMOTION_NOTIFICATION,
      trial_period_ending_notification: (permissions & Permission.TRIAL_PERIOD_ENDING_NOTIFICATION) === Permission.TRIAL_PERIOD_ENDING_NOTIFICATION,
      contract_ending_notification: (permissions & Permission.CONTRACT_ENDING_NOTIFICATION) === Permission.CONTRACT_ENDING_NOTIFICATION,
            internship_request_read: (permissions & Permission.INTERNSHIP_REQUEST_READ) === Permission.INTERNSHIP_REQUEST_READ,
      internship_request_write: (permissions & Permission.INTERNSHIP_REQUEST_WRITE) === Permission.INTERNSHIP_REQUEST_WRITE,
      internship_people_interview_read: (permissions & Permission.INTERNSHIP_PEOPLE_INTERVIEW_READ) === Permission.INTERNSHIP_PEOPLE_INTERVIEW_READ,
      internship_people_interview_write: (permissions & Permission.INTERNSHIP_PEOPLE_INTERVIEW_WRITE) === Permission.INTERNSHIP_PEOPLE_INTERVIEW_WRITE,
      internship_technical_interview_read: (permissions & Permission.INTERNSHIP_TECHNICAL_INTERVIEW_READ) === Permission.INTERNSHIP_TECHNICAL_INTERVIEW_READ,
      internship_technical_interview_write: (permissions & Permission.INTERNSHIP_TECHNICAL_INTERVIEW_WRITE) === Permission.INTERNSHIP_TECHNICAL_INTERVIEW_WRITE,
      internship_documents_read: (permissions & Permission.INTERNSHIP_DOCUMENTS_READ) === Permission.INTERNSHIP_DOCUMENTS_READ,
      internship_documents_write: (permissions & Permission.INTERNSHIP_DOCUMENTS_WRITE) === Permission.INTERNSHIP_DOCUMENTS_WRITE,
      internship_credentials_read: (permissions & Permission.INTERNSHIP_CREDENTIALS_READ) === Permission.INTERNSHIP_CREDENTIALS_READ,
      internship_credentials_write: (permissions & Permission.INTERNSHIP_CREDENTIALS_WRITE) === Permission.INTERNSHIP_CREDENTIALS_WRITE,
      internship_feedback_read: (permissions & Permission.INTERNSHIP_FEEDBACK_READ) === Permission.INTERNSHIP_FEEDBACK_READ,
      internship_feedback_write: (permissions & Permission.INTERNSHIP_FEEDBACK_WRITE) === Permission.INTERNSHIP_FEEDBACK_WRITE,
      internship_lists_read: (permissions & Permission.INTERNSHIP_LISTS_READ) === Permission.INTERNSHIP_LISTS_READ,
      internship_lists_write: (permissions & Permission.INTERNSHIP_LISTS_WRITE) === Permission.INTERNSHIP_LISTS_WRITE,
    };
  }

  /**
   * Gets a mapping of permission field names to Permission enum values
   * @returns Object mapping role permission field names to Permission enum values
   */
   getPermissionFieldMapping(): Record<string, bigint> {
    return {
      dashboard_global: Permission.DASHBOARD_GLOBAL,
      dashboard_client: Permission.DASHBOARD_CLIENT,
      dashboard_leader: Permission.DASHBOARD_LEADER,
      dashboard_language: Permission.DASHBOARD_LANGUAGE,
      dashboard_promotion: Permission.DASHBOARD_PROMOTION,
      dashboard_medical_visit: Permission.DASHBOARD_MEDICAL_VISIT,

      cards_view_read: Permission.CARDS_VIEW_READ,
      cards_view_write: Permission.CARDS_VIEW_WRITE,


      overview_read: Permission.CARD_VIEW_OVERVIEW_READ,
      overview_write: Permission.CARD_VIEW_OVERVIEW_WRITE,
      basic_employee_info: Permission.CARD_VIEW_BASIC_EMPLOYEE_INFO,

      civil_state_details_read:
        Permission.CARD_VIEW_CIVIL_STATE_DETAILS_READ,
      civil_state_details_write:
        Permission.CARD_VIEW_CIVIL_STATE_DETAILS_WRITE,

      location_details_read:
        Permission.CARD_VIEW_LOCATION_DETAILS_READ,
      location_details_write:
        Permission.CARD_VIEW_LOCATION_DETAILS_WRITE,

      social_security_details_read:
        Permission.CARD_VIEW_SOCIAL_SECURITY_DETAILS_READ,
      social_security_details_write:
        Permission.CARD_VIEW_SOCIAL_SECURITY_DETAILS_WRITE,

      contract_details_read:
        Permission.CARD_VIEW_CONTRACT_DETAILS_READ,
      contract_details_write:
        Permission.CARD_VIEW_CONTRACT_DETAILS_WRITE,

      career_details_read:
        Permission.CARD_VIEW_CAREER_DETAILS,
      career_details_write:
        Permission.CARD_VIEW_CAREER_DETAILS_WRITE,

      education_read:
        Permission.CARD_VIEW_EDUCATION_READ,
      education_write:
        Permission.CARD_VIEW_EDUCATION_WRITE,

      languages_read:
        Permission.CARD_VIEW_LANGUAGES_READ,
      languages_write:
        Permission.CARD_VIEW_LANGUAGES_WRITE,

      financial_details_read:
        Permission.CARD_VIEW_FINANCIAL_DETAILS_READ,
      financial_details_write:
        Permission.CARD_VIEW_FINANCIAL_DETAILS_WRITE,

      salary_advances_read:
      Permission.CARD_VIEW_SALARY_ADVANCES_READ,
      salary_advances_write:
      Permission.CARD_VIEW_SALARY_ADVANCES_WRITE,

            afma_read:
        Permission.CARD_VIEW_AFMA_READ,
      afma_write:
        Permission.CARD_VIEW_AFMA_WRITE,
      documents_read:
        Permission.CARD_VIEW_DOCUMENTS_READ,
      documents_write:
        Permission.CARD_VIEW_DOCUMENTS_WRITE,

      equipment_read:
        Permission.CARD_VIEW_EQUIPMENT_READ,
      equipment_write:
        Permission.CARD_VIEW_EQUIPMENT_WRITE,

      medical_visit_read:
        Permission.CARD_VIEW_MEDICAL_VISIT_READ,
      medical_visit_write:
        Permission.CARD_VIEW_MEDICAL_VISIT_WRITE,

      pay_slip_read:
        Permission.CARD_VIEW_PAY_SLIP_READ,
      pay_slip_write:
        Permission.CARD_VIEW_PAY_SLIP_WRITE,

      certifications_download:
        Permission.CARD_VIEW_CERTIFICATIONS_DOWNLOAD,

      skills_read:
        Permission.CARD_VIEW_SKILLS_READ,
      skills_write:
        Permission.CARD_VIEW_SKILLS_WRITE,

      card_view_validate_role:
        Permission.CARD_VIEW_VALIDATE_ROLE,
      own_page:
        Permission.CARD_VIEW_OWN_PAGE,

      client_read: Permission.CLIENT_READ,
      client_write: Permission.CLIENT_WRITE,

      project_read: Permission.PROJECT_READ,
      project_write: Permission.PROJECT_WRITE,

      staffing_read: Permission.STAFFING_READ,
      staffing_write: Permission.STAFFING_WRITE,

      promotion_read: Permission.PROMOTION_READ,
      promotion_identify_candidate: Permission.PROMOTION_IDENTIFY_CANDIDATE,
      promotion_confirm_identification:
        Permission.PROMOTION_CONFIRM_IDENTIFICATION,
      promotion_schedule_interview:
        Permission.PROMOTION_SCHEDULE_INTERVIEW,
      promotion_manage_candidate:
        Permission.PROMOTION_MANAGE_CANDIDATE,
      promotion_commit: Permission.PROMOTION_COMMIT,

      evaluation_read: Permission.EVALUATION_READ,
      evaluation_write: Permission.EVALUATION_WRITE,
      evluation_assign_reviewers: Permission.EVLUATION_ASSIGN_REVIEWERS,

      pool_read: Permission.POOL_READ,
      pool_write: Permission.POOL_WRITE,

      manager_read: Permission.MANAGER_READ,
      manager_write: Permission.MANAGER_WRITE,
      manager_people: Permission.MANAGER_PEOPLE,
      bsa: Permission.BSA,
      production: Permission.PRODUCTION,

      people_read: Permission.PEOPLE_READ,
      people_write: Permission.PEOPLE_WRITE,

      director_read: Permission.DIRECTOR_READ,
      director_write: Permission.DIRECTOR_WRITE,

      notification_read: Permission.NOTIFICATION_READ,

      salaries_read: Permission.SALARIES_READ,
      salaries_write: Permission.SALARIES_WRITE,
      salary_validate: Permission.SALARY_VALIDATE,

      config_staffing: Permission.CONFIG_STAFFING,
      config_bsa: Permission.CONFIG_BSA,
      config_invoicing: Permission.CONFIG_INVOICING,
      config_performance: Permission.CONFIG_PERFORMANCE,
      config_user_management:
        Permission.CONFIG_USER_MANAGEMENT,

      invoicing_read: Permission.INVOICING_READ,
      invoicing_service: Permission.INVOICING_SERVICE,
      invoicing_admin: Permission.INVOICING_ADMIN,
      extra_oncall_export: Permission.Extra_OnCall_Export,

      export_import_import_employee: Permission.EXPORT_IMPORT_IMPORT_EMPLOYEE,
      export_import_import_employees: Permission.EXPORT_IMPORT_IMPORT_EMPLOYEES,
      export_import_missing_documents: Permission.EXPORT_IMPORT_MISSING_DOCUMENTS,
      export_import_seniority: Permission.EXPORT_IMPORT_SENIORITY,
      export_import_afma: Permission.EXPORT_IMPORT_AFMA,
      export_import_medical_visit: Permission.EXPORT_IMPORT_MEDICAL_VISIT,
      export_import_school_grant: Permission.EXPORT_IMPORT_SCHOOL_GRANT,
      export_import_staffing_bsa: Permission.EXPORT_IMPORT_STAFFING_BSA,
      export_import_staffing_production: Permission.EXPORT_IMPORT_STAFFING_PRODUCTION,
      export_import_pay_slip: Permission.EXPORT_IMPORT_PAY_SLIP,
      export_import_salary: Permission.EXPORT_IMPORT_SALARY,
      config_export_import: Permission.CONFIG_EXPORT_IMPORT,

      bsa_org_read: Permission.BSA_ORG_READ,
      bsa_org_write: Permission.BSA_ORG_WRITE,

      audit_log_read: Permission.AUDIT_LOG_READ,

      opportunity_read: Permission.OPPORTUNITY_READ,
      opportunity_write: Permission.OPPORTUNITY_WRITE,

      demand_read: Permission.DEMAND_READ,
      demand_write: Permission.DEMAND_WRITE,

      people_at_risk_read: Permission.PEOPLE_AT_RISK_READ,
      people_at_risk_write: Permission.PEOPLE_AT_RISK_WRITE,

      billing_read: Permission.BILLING_READ,
      billing_write: Permission.BILLING_WRITE,

      people_with_bad_attitude_read:
        Permission.PEOPLE_WITH_BAD_ATTITUDE_READ,
      people_with_bad_attitude_write:
        Permission.PEOPLE_WITH_BAD_ATTITUDE_WRITE,

      feedback_assigned_handler:
        Permission.FEEDBACK_ASSIGNED_HANDLER,
      feedback_assignment_manager:
        Permission.FEEDBACK_ASSIGNMENT_MANAGER,
      junior_promotion_notification:
        Permission.JUNIOR_PROMOTION_NOTIFICATION,
      trial_period_ending_notification:
        Permission.TRIAL_PERIOD_ENDING_NOTIFICATION,
      contract_ending_notification:
        Permission.CONTRACT_ENDING_NOTIFICATION,
      internship_request_read: Permission.INTERNSHIP_REQUEST_READ,
      internship_request_write: Permission.INTERNSHIP_REQUEST_WRITE,
      internship_people_interview_read: Permission.INTERNSHIP_PEOPLE_INTERVIEW_READ,
      internship_people_interview_write: Permission.INTERNSHIP_PEOPLE_INTERVIEW_WRITE,
      internship_technical_interview_read: Permission.INTERNSHIP_TECHNICAL_INTERVIEW_READ,
      internship_technical_interview_write: Permission.INTERNSHIP_TECHNICAL_INTERVIEW_WRITE,
      internship_documents_read: Permission.INTERNSHIP_DOCUMENTS_READ,
      internship_documents_write: Permission.INTERNSHIP_DOCUMENTS_WRITE,
      internship_credentials_read: Permission.INTERNSHIP_CREDENTIALS_READ,
      internship_credentials_write: Permission.INTERNSHIP_CREDENTIALS_WRITE,
      internship_feedback_read: Permission.INTERNSHIP_FEEDBACK_READ,
      internship_feedback_write: Permission.INTERNSHIP_FEEDBACK_WRITE,
      internship_lists_read: Permission.INTERNSHIP_LISTS_READ,
      internship_lists_write: Permission.INTERNSHIP_LISTS_WRITE,
    };
  }
  /**
   * Validates if a permission field name is valid
   * @param fieldName The permission field name to validate
   * @returns True if the field name is valid
   */
  isValidPermissionField(fieldName: string): boolean {
    const mapping = this.getPermissionFieldMapping();
    return fieldName in mapping;
  }

  /**
   * Gets the Permission enum value for a specific field
   * @param fieldName The permission field name
   * @returns The corresponding Permission enum value or null if invalid
   */
  getPermissionEnumValue(fieldName: string): bigint | null {
    const mapping = this.getPermissionFieldMapping();
    return mapping[fieldName] || null;
  }

  // ==================== NEW API INTEGRATION METHODS ====================

  /**
   * Load all role permissions from the API
   * @returns Observable of RolePermission array
   */
  loadRolePermissionsFromApi(): Observable<RolePermission[]> {
    return forkJoin({
      roles: this.roleService.getAllRoles(),
      allRolePermissions: this.rolePermissionApiService.getAllRolePermissions()
    }).pipe(
      map(({ roles, allRolePermissions }) => {
        // Remember name -> id so saving does not have to re-fetch every role just
        // to translate the row's name into the id the update endpoint needs.
        this.roleIdByName.clear();
        roles.forEach(role => {
          if (role.id != null) {
            this.roleIdByName.set(role.name, role.id);
          }
        });

        return roles.map(role => {
          const rolePermissionData = allRolePermissions.find(rp => rp.roleId === role.id);
          return this.convertApiDataToRolePermission(role, rolePermissionData?.permissions || []);
        });
      })
    );
  }

  /**
   * Save role permissions to the API
   * @param rolePermissions The role permission object to save
   * @returns Observable of RolePermissionResponse
   */
  saveRolePermissionsToApi(rolePermissions: RolePermission): Observable<RolePermissionResponse> {
    // Saving used to always GET every role first, purely to turn the row's name
    // into an id - a second, fully sequential round trip on every save, and one
    // that also pays for the role list's own permission mapping on the backend.
    // The id was already known from the load, so reuse it when we have it.
    const cachedId = this.roleIdByName.get(rolePermissions.role);
    if (cachedId != null) {
      return this.rolePermissionApiService.updateRolePermissions(
        cachedId,
        this.getEnabledPermissionIds(rolePermissions)
      );
    }

    return this.roleService.getAllRoles().pipe(
      switchMap(roles => {
        const role = roles.find(r => r.name === rolePermissions.role);
        if (!role) {
          throw new Error(`Role ${rolePermissions.role} not found`);
        }

        if (role.id != null) {
          this.roleIdByName.set(role.name, role.id);
        }

        const permissionIds = this.getEnabledPermissionIds(rolePermissions);
        return this.rolePermissionApiService.updateRolePermissions(role.id!, permissionIds);
      })
    );
  }

  /**
   * Get permissions for a specific role from API
   * @param roleName The role name
   * @returns Observable of RolePermission
   */
  getRolePermissionsFromApi(roleName: string): Observable<RolePermission> {
    return this.roleService.getAllRoles().pipe(
      switchMap(roles => {
        const role = roles.find(r => r.name === roleName);
        if (!role) {
          throw new Error(`Role ${roleName} not found`);
        }

        return this.rolePermissionApiService.getRolePermissions(role.id!);
      }),
      map(rolePermissionResponse => {
        return this.convertApiDataToRolePermission(
          { id: rolePermissionResponse.roleId, name: rolePermissionResponse.roleName },
          rolePermissionResponse.permissions
        );
      })
    );
  }

  /**
   * Convert API data to RolePermission format
   * @param role The role object
   * @param permissions Array of permissions from API
   * @returns RolePermission object
   */
  private convertApiDataToRolePermission(role: any, permissions: PermissionModel[]): RolePermission {
    const permissionNames = permissions.map(p => p.name);
    const isSuperAdmin = this.isSuperAdminRole(role?.name);

    return {
      role: role.name,
      dashboard_global: permissionNames.includes('DASHBOARD_GLOBAL'),
      dashboard_client: permissionNames.includes('DASHBOARD_CLIENT'),
      dashboard_leader: permissionNames.includes('DASHBOARD_LEADER'),
      dashboard_language: permissionNames.includes('DASHBOARD_LANGUAGE'),
      dashboard_promotion: permissionNames.includes('DASHBOARD_PROMOTION'),
      dashboard_medical_visit: permissionNames.includes('DASHBOARD_MEDICAL_VISIT'),
      cards_view_read: permissionNames.includes('CARDS_VIEW_READ'),
      cards_view_write: permissionNames.includes('CARDS_VIEW_WRITE'),
      overview_read: permissionNames.includes('CARD_VIEW_OVERVIEW_READ'),
      overview_write:
        permissionNames.includes('CARD_VIEW_OVERVIEW_WRITE'),
      basic_employee_info:
        permissionNames.includes('CARD_VIEW_BASIC_EMPLOYEE_INFO'),

      civil_state_details_read:
        permissionNames.includes('CARD_VIEW_CIVIL_STATE_DETAILS_READ'),
      civil_state_details_write:
        permissionNames.includes('CARD_VIEW_CIVIL_STATE_DETAILS_WRITE'),

      location_details_read:
        permissionNames.includes('CARD_VIEW_LOCATION_DETAILS_READ'),
      location_details_write:
        permissionNames.includes('CARD_VIEW_LOCATION_DETAILS_WRITE'),

      social_security_details_read:
        permissionNames.includes('CARD_VIEW_SOCIAL_SECURITY_DETAILS_READ'),
      social_security_details_write:
        permissionNames.includes('CARD_VIEW_SOCIAL_SECURITY_DETAILS_WRITE'),

      contract_details_read:
        permissionNames.includes('CARD_VIEW_CONTRACT_DETAILS_READ'),
      contract_details_write:
        permissionNames.includes('CARD_VIEW_CONTRACT_DETAILS_WRITE'),

      career_details_read:
        permissionNames.includes('CARD_VIEW_CAREER_DETAILS'),
      career_details_write:
        permissionNames.includes('CARD_VIEW_CAREER_DETAILS_WRITE'),

      education_read:
        permissionNames.includes('CARD_VIEW_EDUCATION_READ'),
      education_write:
        permissionNames.includes('CARD_VIEW_EDUCATION_WRITE'),

      languages_read:
        permissionNames.includes('CARD_VIEW_LANGUAGES_READ'),
      languages_write:
        permissionNames.includes('CARD_VIEW_LANGUAGES_WRITE'),

      financial_details_read:
        permissionNames.includes('CARD_VIEW_FINANCIAL_DETAILS_READ'),
      financial_details_write:
        permissionNames.includes('CARD_VIEW_FINANCIAL_DETAILS_WRITE'),

      salary_advances_read:
        permissionNames.includes('CARD_VIEW_SALARY_ADVANCES_READ'),
      salary_advances_write:
        permissionNames.includes('CARD_VIEW_SALARY_ADVANCES_WRITE'),
      afma_read:
        permissionNames.includes('CARD_VIEW_AFMA_READ'),
      afma_write:
        permissionNames.includes('CARD_VIEW_AFMA_WRITE'),
      documents_read:
        permissionNames.includes('CARD_VIEW_DOCUMENTS_READ'),
      documents_write:
        permissionNames.includes('CARD_VIEW_DOCUMENTS_WRITE'),

      equipment_read:
        permissionNames.includes('CARD_VIEW_EQUIPMENT_READ'),
      equipment_write:
        permissionNames.includes('CARD_VIEW_EQUIPMENT_WRITE'),

      medical_visit_read:
        permissionNames.includes('CARD_VIEW_MEDICAL_VISIT_READ'),
      medical_visit_write:
        permissionNames.includes('CARD_VIEW_MEDICAL_VISIT_WRITE'),

      pay_slip_read:
        permissionNames.includes('CARD_VIEW_PAY_SLIP_READ'),
      pay_slip_write:
        permissionNames.includes('CARD_VIEW_PAY_SLIP_WRITE'),

      certifications_download:
        permissionNames.includes('CARD_VIEW_CERTIFICATIONS_DOWNLOAD'),

      skills_read:
        permissionNames.includes('CARD_VIEW_SKILLS_READ'),
      skills_write:
        permissionNames.includes('CARD_VIEW_SKILLS_WRITE'),
      card_view_validate_role: permissionNames.includes('CARD_VIEW_VALIDATE_ROLE'),
      own_page: permissionNames.includes('CARD_VIEW_OWN_PAGE'),
      client_read: permissionNames.includes('CLIENT_READ'),
      client_write: permissionNames.includes('CLIENT_WRITE'),
      project_read: permissionNames.includes('PROJECT_READ'),
      project_write: permissionNames.includes('PROJECT_WRITE'),
      staffing_read: permissionNames.includes('STAFFING_READ'),
      staffing_write: permissionNames.includes('STAFFING_WRITE'),
      promotion_read: permissionNames.includes('PROMOTION_READ'),
      promotion_identify_candidate: permissionNames.includes('PROMOTION_IDENTIFY_CANDIDATE'),
      promotion_confirm_identification: permissionNames.includes('PROMOTION_CONFIRM_IDENTIFICATION'),
      promotion_schedule_interview: permissionNames.includes('PROMOTION_SCHEDULE_INTERVIEW'),
      promotion_manage_candidate: permissionNames.includes('PROMOTION_MANAGE_CANDIDATE'),
      promotion_commit:permissionNames.includes('PROMOTION_COMMIT'),
      evaluation_read: permissionNames.includes('EVALUATION_READ'),
      evaluation_write: permissionNames.includes('EVALUATION_WRITE'),
      evluation_assign_reviewers: permissionNames.includes('EVLUATION_ASSIGN_REVIEWERS'),
      pool_read: permissionNames.includes('POOL_READ'),
      pool_write: permissionNames.includes('POOL_WRITE'),
      manager_read: permissionNames.includes('MANAGER_READ'),
      manager_write: permissionNames.includes('MANAGER_WRITE'),
      manager_people: permissionNames.includes('MANAGER_PEOPLE'),
      bsa: permissionNames.includes('BSA'),
      production: permissionNames.includes('PRODUCTION'),
      people_read: permissionNames.includes('PEOPLE_READ'),
      people_write: permissionNames.includes('PEOPLE_WRITE'),
      director_read: permissionNames.includes('DIRECTOR_READ'),
      director_write: permissionNames.includes('DIRECTOR_WRITE'),
      notification_read: permissionNames.includes('NOTIFICATION_READ'),
      junior_promotion_notification: permissionNames.includes('JUNIOR_PROMOTION_NOTIFICATION'),
      trial_period_ending_notification: permissionNames.includes('TRIAL_PERIOD_ENDING_NOTIFICATION'),
      contract_ending_notification: permissionNames.includes('CONTRACT_ENDING_NOTIFICATION'),
      internship_request_read: permissionNames.includes('INTERNSHIP_REQUEST_READ'),
      internship_request_write: permissionNames.includes('INTERNSHIP_REQUEST_WRITE'),
      internship_people_interview_read: permissionNames.includes('INTERNSHIP_PEOPLE_INTERVIEW_READ'),
      internship_people_interview_write: permissionNames.includes('INTERNSHIP_PEOPLE_INTERVIEW_WRITE'),
      internship_technical_interview_read: permissionNames.includes('INTERNSHIP_TECHNICAL_INTERVIEW_READ'),
      internship_technical_interview_write: permissionNames.includes('INTERNSHIP_TECHNICAL_INTERVIEW_WRITE'),
      internship_documents_read: permissionNames.includes('INTERNSHIP_DOCUMENTS_READ'),
      internship_documents_write: permissionNames.includes('INTERNSHIP_DOCUMENTS_WRITE'),
      internship_credentials_read: permissionNames.includes('INTERNSHIP_CREDENTIALS_READ'),
      internship_credentials_write: permissionNames.includes('INTERNSHIP_CREDENTIALS_WRITE'),
      internship_feedback_read: permissionNames.includes('INTERNSHIP_FEEDBACK_READ'),
      internship_feedback_write: permissionNames.includes('INTERNSHIP_FEEDBACK_WRITE'),
      internship_lists_read: permissionNames.includes('INTERNSHIP_LISTS_READ'),
      internship_lists_write: permissionNames.includes('INTERNSHIP_LISTS_WRITE'),
      salaries_read: permissionNames.includes('SALARIES_READ'),
      salaries_write: permissionNames.includes('SALARIES_WRITE'),
      salary_validate: permissionNames.includes('SALARY_VALIDATE'),
      config_staffing: permissionNames.includes('CONFIG_STAFFING'),
      config_bsa: permissionNames.includes('CONFIG_BSA'),
      config_invoicing: permissionNames.includes('CONFIG_INVOICING'),
      config_performance: permissionNames.includes('CONFIG_PERFORMANCE'),
      config_user_management: permissionNames.includes('CONFIG_USER_MANAGEMENT'),
      invoicing_read: permissionNames.includes('INVOICING_READ'),
      invoicing_service: permissionNames.includes('INVOICING_SERVICE'),
      invoicing_admin: permissionNames.includes('INVOICING_ADMIN'),
      extra_oncall_export: permissionNames.includes('Extra_OnCall_Export'),

      export_import_import_employee: permissionNames.includes('EXPORT_IMPORT_IMPORT_EMPLOYEE'),
      export_import_import_employees: permissionNames.includes('EXPORT_IMPORT_IMPORT_EMPLOYEES'),
      export_import_missing_documents: permissionNames.includes('EXPORT_IMPORT_MISSING_DOCUMENTS'),
      export_import_seniority: permissionNames.includes('EXPORT_IMPORT_SENIORITY'),
      export_import_afma: permissionNames.includes('EXPORT_IMPORT_AFMA'),
      export_import_medical_visit: permissionNames.includes('EXPORT_IMPORT_MEDICAL_VISIT'),
      export_import_school_grant: permissionNames.includes('EXPORT_IMPORT_SCHOOL_GRANT'),
      export_import_staffing_bsa: permissionNames.includes('EXPORT_IMPORT_STAFFING_BSA'),
      export_import_staffing_production: permissionNames.includes('EXPORT_IMPORT_STAFFING_PRODUCTION'),
      export_import_pay_slip: permissionNames.includes('EXPORT_IMPORT_PAY_SLIP'),
      export_import_salary: permissionNames.includes('EXPORT_IMPORT_SALARY'),
      config_export_import: permissionNames.includes('CONFIG_EXPORT_IMPORT'),
      audit_log_read: permissionNames.includes('AUDIT_LOG_READ'),
      bsa_org_read: isSuperAdmin || permissionNames.includes('BSA_ORG_READ'),
      bsa_org_write: isSuperAdmin || permissionNames.includes('BSA_ORG_WRITE'),
      opportunity_read: isSuperAdmin || permissionNames.includes('OPPORTUNITY_READ'),
      opportunity_write: isSuperAdmin || permissionNames.includes('OPPORTUNITY_WRITE'),
      demand_read: isSuperAdmin || permissionNames.includes('DEMAND_READ'),
      demand_write: isSuperAdmin || permissionNames.includes('DEMAND_WRITE'),
      people_at_risk_read: isSuperAdmin || permissionNames.includes('PEOPLE_AT_RISK_READ'),
      people_at_risk_write: isSuperAdmin || permissionNames.includes('PEOPLE_AT_RISK_WRITE'),
      billing_read: isSuperAdmin || permissionNames.includes('BILLING_READ'),
      billing_write: isSuperAdmin || permissionNames.includes('BILLING_WRITE'),
      people_with_bad_attitude_read: isSuperAdmin || permissionNames.includes('PEOPLE_WITH_BAD_ATTITUDE_READ'),
      people_with_bad_attitude_write: isSuperAdmin || permissionNames.includes('PEOPLE_WITH_BAD_ATTITUDE_WRITE'),
      feedback_assigned_handler: isSuperAdmin || permissionNames.includes('FEEDBACK_ASSIGNED_HANDLER'),
      feedback_assignment_manager: isSuperAdmin || permissionNames.includes('FEEDBACK_ASSIGNMENT_MANAGER'),
    };
  }

  private isSuperAdminRole(roleName: string | undefined): boolean {
    if (!roleName) {
      return false;
    }

    return roleName.toLowerCase().replace(/[_\s]+/g, '') === 'superadmin';
  }

  /**
   * Loads the permission name to ID mapping from the backend API.
   */
  private loadPermissionNameToIdMap(): void {
    this.permissionApiService.getAllPermissions().subscribe((permissions) => {
      this.permissionNameToIdMap = {};
      for (const perm of permissions) {
        this.permissionNameToIdMap[perm.name] = perm.id;
      }
      this.permissionsLoaded = true;
    });
  }

  getEnabledPermissionIds(rolePermissions: RolePermission): number[] {
    if (!this.permissionsLoaded) {
      console.warn('Permission name-to-ID map not loaded yet.');
      return [];
    }

    const enabledPermissions: string[] = [];

    Object.entries(rolePermissions).forEach(([key, value]) => {
      if (key !== 'role' && value === true) {
        const permissionNames = this.convertFieldNameToPermissionNames(key);
        if (permissionNames.length > 0) {
          enabledPermissions.push(...permissionNames);
        }
      }
    });

    return [...new Set(enabledPermissions)]
      .map(name => this.permissionNameToIdMap[name])
      .filter(id => id !== undefined);
  }

  /**
   * Convert field name to permission name
   * @param fieldName The field name from RolePermission
   * @returns The corresponding permission name
   */
  private convertFieldNameToPermissionNames(fieldName: string): string[] {
    const mapping: Record<string, string | string[]> = {
      'dashboard_global': 'DASHBOARD_GLOBAL',
      'dashboard_client': 'DASHBOARD_CLIENT',
      'dashboard_leader': 'DASHBOARD_LEADER',
      'dashboard_language': 'DASHBOARD_LANGUAGE',
      'dashboard_promotion': 'DASHBOARD_PROMOTION',
      'dashboard_medical_visit': 'DASHBOARD_MEDICAL_VISIT',
      'cards_view_read': 'CARDS_VIEW_READ',
      'cards_view_write': 'CARDS_VIEW_WRITE',
      'overview_read': 'CARD_VIEW_OVERVIEW_READ',
      'overview_write': 'CARD_VIEW_OVERVIEW_WRITE',
      'basic_employee_info': 'CARD_VIEW_BASIC_EMPLOYEE_INFO',

      'civil_state_details_read':
        'CARD_VIEW_CIVIL_STATE_DETAILS_READ',
      'civil_state_details_write':
        'CARD_VIEW_CIVIL_STATE_DETAILS_WRITE',

      'location_details_read':
        'CARD_VIEW_LOCATION_DETAILS_READ',
      'location_details_write':
        'CARD_VIEW_LOCATION_DETAILS_WRITE',

      'social_security_details_read':
        'CARD_VIEW_SOCIAL_SECURITY_DETAILS_READ',
      'social_security_details_write':
        'CARD_VIEW_SOCIAL_SECURITY_DETAILS_WRITE',

      'contract_details_read':
        'CARD_VIEW_CONTRACT_DETAILS_READ',
      'contract_details_write':
        'CARD_VIEW_CONTRACT_DETAILS_WRITE',

      'career_details_read':
        'CARD_VIEW_CAREER_DETAILS',
      'career_details_write':
        'CARD_VIEW_CAREER_DETAILS_WRITE',

      'education_read':
        'CARD_VIEW_EDUCATION_READ',
      'education_write':
        'CARD_VIEW_EDUCATION_WRITE',

      'languages_read':
        'CARD_VIEW_LANGUAGES_READ',
      'languages_write':
        'CARD_VIEW_LANGUAGES_WRITE',

      'financial_details_read':
        'CARD_VIEW_FINANCIAL_DETAILS_READ',
      'financial_details_write':
        'CARD_VIEW_FINANCIAL_DETAILS_WRITE',

      'salary_advances_read':
       'CARD_VIEW_SALARY_ADVANCES_READ',
      'salary_advances_write':
        'CARD_VIEW_SALARY_ADVANCES_WRITE',
      'afma_read':
        'CARD_VIEW_AFMA_READ',
      'afma_write':
        'CARD_VIEW_AFMA_WRITE',
      'documents_read':
        'CARD_VIEW_DOCUMENTS_READ',
      'documents_write':
        'CARD_VIEW_DOCUMENTS_WRITE',

      'equipment_read':
        'CARD_VIEW_EQUIPMENT_READ',
      'equipment_write':
        'CARD_VIEW_EQUIPMENT_WRITE',

      'medical_visit_read':
        'CARD_VIEW_MEDICAL_VISIT_READ',
      'medical_visit_write':
        'CARD_VIEW_MEDICAL_VISIT_WRITE',

      'pay_slip_read':
        'CARD_VIEW_PAY_SLIP_READ',
      'pay_slip_write':
        'CARD_VIEW_PAY_SLIP_WRITE',

      'certifications_download':
        'CARD_VIEW_CERTIFICATIONS_DOWNLOAD',

      'skills_read':
        'CARD_VIEW_SKILLS_READ',
      'skills_write':
        'CARD_VIEW_SKILLS_WRITE',
      'card_view_validate_role': 'CARD_VIEW_VALIDATE_ROLE',
      'own_page': 'CARD_VIEW_OWN_PAGE',
      'client_read': 'CLIENT_READ',
      'client_write': 'CLIENT_WRITE',
      'project_read': 'PROJECT_READ',
      'project_write': 'PROJECT_WRITE',
      'staffing_read': 'STAFFING_READ',
      'staffing_write': 'STAFFING_WRITE',
      'promotion_read': 'PROMOTION_READ',
      'promotion_identify_candidate': 'PROMOTION_IDENTIFY_CANDIDATE',
      'promotion_confirm_identification': 'PROMOTION_CONFIRM_IDENTIFICATION',
      'promotion_schedule_interview': 'PROMOTION_SCHEDULE_INTERVIEW',
      'promotion_manage_candidate': 'PROMOTION_MANAGE_CANDIDATE',
      'promotion_commit': 'PROMOTION_COMMIT',
      'evaluation_read': 'EVALUATION_READ',
      'evaluation_write': 'EVALUATION_WRITE',
      'evluation_assign_reviewers': 'EVLUATION_ASSIGN_REVIEWERS',
      'pool_read': 'POOL_READ',
      'pool_write': 'POOL_WRITE',
      'manager_read': 'MANAGER_READ',
      'manager_write': 'MANAGER_WRITE',
      'manager_people': 'MANAGER_PEOPLE',
      'bsa': 'BSA',
      'production': 'PRODUCTION',
      'people_read': 'PEOPLE_READ',
      'people_write': 'PEOPLE_WRITE',
      'director_read': 'DIRECTOR_READ',
      'director_write': 'DIRECTOR_WRITE',
      'notification_read': 'NOTIFICATION_READ',
      'junior_promotion_notification': 'JUNIOR_PROMOTION_NOTIFICATION',
      'trial_period_ending_notification': 'TRIAL_PERIOD_ENDING_NOTIFICATION',
      'contract_ending_notification': 'CONTRACT_ENDING_NOTIFICATION',
      'salaries_read': 'SALARIES_READ',
      'salaries_write': 'SALARIES_WRITE',
      'salary_validate': 'SALARY_VALIDATE',
      'config_staffing': 'CONFIG_STAFFING',
      'config_bsa': 'CONFIG_BSA',
      'config_invoicing': 'CONFIG_INVOICING',
      'config_performance': 'CONFIG_PERFORMANCE',
      'config_user_management': 'CONFIG_USER_MANAGEMENT',
      'invoicing_read': ['INVOICING_READ'],
      'invoicing_service': ['INVOICING_READ', 'INVOICING_SERVICE'],
      'invoicing_admin': ['INVOICING_READ', 'INVOICING_SERVICE', 'INVOICING_ADMIN'],
      'extra_oncall_export': ['Extra_OnCall_Export'],
      'export_import_import_employee': 'EXPORT_IMPORT_IMPORT_EMPLOYEE',
      'export_import_import_employees': 'EXPORT_IMPORT_IMPORT_EMPLOYEES',
      'export_import_missing_documents': 'EXPORT_IMPORT_MISSING_DOCUMENTS',
      'export_import_seniority': 'EXPORT_IMPORT_SENIORITY',
      'export_import_afma': 'EXPORT_IMPORT_AFMA',
      'export_import_medical_visit': 'EXPORT_IMPORT_MEDICAL_VISIT',
      'export_import_school_grant': 'EXPORT_IMPORT_SCHOOL_GRANT',
      'export_import_staffing_bsa': 'EXPORT_IMPORT_STAFFING_BSA',
      'export_import_staffing_production': 'EXPORT_IMPORT_STAFFING_PRODUCTION',
      'export_import_pay_slip': 'EXPORT_IMPORT_PAY_SLIP',
      'export_import_salary': 'EXPORT_IMPORT_SALARY',
      'config_export_import': 'CONFIG_EXPORT_IMPORT',
      'bsa_org_read': 'BSA_ORG_READ',
      'bsa_org_write': 'BSA_ORG_WRITE',
      'audit_log_read': 'AUDIT_LOG_READ',
      'opportunity_read': 'OPPORTUNITY_READ',
      'opportunity_write': 'OPPORTUNITY_WRITE',
      'demand_read': 'DEMAND_READ',
      'demand_write': 'DEMAND_WRITE',
      'people_at_risk_read': 'PEOPLE_AT_RISK_READ',
      'people_at_risk_write': 'PEOPLE_AT_RISK_WRITE',
      'billing_read': 'BILLING_READ',
      'billing_write': 'BILLING_WRITE',
      'people_with_bad_attitude_read': 'PEOPLE_WITH_BAD_ATTITUDE_READ',
      'people_with_bad_attitude_write': 'PEOPLE_WITH_BAD_ATTITUDE_WRITE',
      'feedback_assigned_handler': 'FEEDBACK_ASSIGNED_HANDLER',
      'feedback_assignment_manager': 'FEEDBACK_ASSIGNMENT_MANAGER',
      'internship_request_read': 'INTERNSHIP_REQUEST_READ',
      'internship_request_write': ['INTERNSHIP_REQUEST_READ', 'INTERNSHIP_REQUEST_WRITE'],
      'internship_people_interview_read': 'INTERNSHIP_PEOPLE_INTERVIEW_READ',
      'internship_people_interview_write': ['INTERNSHIP_PEOPLE_INTERVIEW_READ', 'INTERNSHIP_PEOPLE_INTERVIEW_WRITE'],
      'internship_technical_interview_read': 'INTERNSHIP_TECHNICAL_INTERVIEW_READ',
      'internship_technical_interview_write': ['INTERNSHIP_TECHNICAL_INTERVIEW_READ', 'INTERNSHIP_TECHNICAL_INTERVIEW_WRITE'],
      'internship_documents_read': 'INTERNSHIP_DOCUMENTS_READ',
      'internship_documents_write': ['INTERNSHIP_DOCUMENTS_READ', 'INTERNSHIP_DOCUMENTS_WRITE'],
      'internship_credentials_read': 'INTERNSHIP_CREDENTIALS_READ',
      'internship_credentials_write': ['INTERNSHIP_CREDENTIALS_READ', 'INTERNSHIP_CREDENTIALS_WRITE'],
      'internship_feedback_read': 'INTERNSHIP_FEEDBACK_READ',
      'internship_feedback_write': ['INTERNSHIP_FEEDBACK_READ', 'INTERNSHIP_FEEDBACK_WRITE'],
      'internship_lists_read': 'INTERNSHIP_LISTS_READ',
      'internship_lists_write': ['INTERNSHIP_LISTS_READ', 'INTERNSHIP_LISTS_WRITE'],
    };

    const permissionNames = mapping[fieldName];
    if (!permissionNames) {
      return [];
    }

    return Array.isArray(permissionNames) ? permissionNames : [permissionNames];
  }
}
